import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import moment from "jalali-moment";
import DeleteIcon from "@mui/icons-material/Delete";
import { BootstrapTooltip, calculateBalance } from "../../../utilities";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Badge, Chip } from "@mui/material";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import renderCellExpand from "src/GridComponents/GridExpand";
import GroupsIcon from "@mui/icons-material/Groups";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SecurityIcon from "@mui/icons-material/Security";
import { IAppUser } from "src/interface/IUser";
import { usePermissions } from "src/utilities/permissions";

export const userColumns = ({
  handleShowDetailClick,
  handleDeleteClick,
  handleWorkReportClick,
  handleCopyClick,
  handlePermissionClick,
  getTags,
  getBaseActivities,
  getRole,
  canView,
  canEdit,
  canDelete,
  canCreate,
}: {
  handleShowDetailClick: (id: string) => () => void;
  handleDeleteClick: any;
  handleWorkReportClick?: (userId: string, userName: string) => () => void;
  handleCopyClick?: (userData: IAppUser) => () => void;
  handlePermissionClick?: (userId: string, userName: string) => () => void;
  getTags: any[];
  getBaseActivities: IAutoCompleteLabelValue[];
  getRole: string;
  canView: (menuId: string) => boolean;
  canEdit: (menuId: string) => boolean;
  canDelete: (menuId: string) => boolean;
  canCreate: (menuId: string) => boolean;
}): GridColDef[] => {
  return [
    {
      field: "actions",
      type: "actions",
      headerAlign: "center",
      headerName: "عملیات",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "actions",
      minWidth: 70,
      width: 70,
      renderCell: (params: any) => (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "auto auto auto",
            gap: "0px 0px",
            width: "100%",
          }}
        >
          {canView(getRole === "Customer" ? "customers" : "employees") && (
            <BootstrapTooltip title="مشاهده اطلاعات مشتری">
              <GridActionsCellItem
                icon={<VisibilityIcon color="primary" />}
                label="مشاهده اطلاعات مشتری"
                className="textPrimary"
                onClick={handleShowDetailClick(params.id)}
                color="inherit"
              />
            </BootstrapTooltip>
          )}
          {handleCopyClick &&
            getRole === "Customer" &&
            canView("customers") && (
              <BootstrapTooltip
                title={
                  params.row.childs?.length > 0 ? (
                    <div>
                      {params.row.childs?.map(
                        (item: IAppUser, index: number) => (
                          <div key={index}>
                            <p style={{ fontSize: 14 }}>
                              {item.fName}
                              {"      "} {item.lName} {"      "}{" "}
                              {item.phoneNumber}
                            </p>
                            {index < params.row.childs.length - 1 && <br />}{" "}
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    "افراد مرتبط ندارد"
                  )
                }
              >
                <Badge badgeContent={params.row.childs?.length} color="primary">
                  <GridActionsCellItem
                    icon={<GroupsIcon color="secondary" />}
                    label="افراد مرتبط"
                    color="inherit"
                  />
                </Badge>
              </BootstrapTooltip>
            )}
          {canDelete(getRole === "Customer" ? "customers" : "employees") && (
            <BootstrapTooltip title="حذف">
              <GridActionsCellItem
                icon={<DeleteIcon color="error" />}
                label="حذف"
                onClick={handleDeleteClick(params.id.toString())}
                color="inherit"
              />
            </BootstrapTooltip>
          )}
          {handleCopyClick &&
            getRole === "Customer" &&
            canCreate("customers") && (
              <BootstrapTooltip title="کپی کاربر">
                <GridActionsCellItem
                  icon={<ContentCopyIcon color="success" />}
                  label="کپی کاربر"
                  onClick={handleCopyClick(params.row)}
                  color="inherit"
                />
              </BootstrapTooltip>
            )}
          {handleWorkReportClick &&
            getRole === "Editor" &&
            canView("reports") && (
              <BootstrapTooltip title="گزارش کارکرد">
                <GridActionsCellItem
                  icon={<AssessmentIcon color="info" />}
                  label="گزارش کارکرد"
                  onClick={handleWorkReportClick(
                    params.id.toString(),
                    `${params.row.fName} ${params.row.lName}`
                  )}
                  color="inherit"
                />
              </BootstrapTooltip>
            )}

          {handlePermissionClick &&
            (getRole === "Editor" || getRole === "all" || getRole === "All") &&
            canEdit("employees") && (
              <BootstrapTooltip title="سطح دسترسی">
                <GridActionsCellItem
                  icon={<SecurityIcon color="secondary" />}
                  label="سطح دسترسی"
                  onClick={handlePermissionClick(
                    params.id.toString(),
                    `${params.row.fName} ${params.row.lName}`
                  )}
                  color="inherit"
                />
              </BootstrapTooltip>
            )}
        </div>
      ),
    },
    {
      field: "userName",
      headerName: "نام کاربری",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      minWidth: 110,
      cellClassName: "center-vertical",
    },
    {
      field: "fullNameAndCompany",
      headerName: "نام و نام خانوادگی / شرکت",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 200,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) => {
        if (!params.value) {
          return <span style={{ color: "#999", fontSize: "12px" }}>-</span>;
        }

        const lines = params.value.split("\n");
        const fullName = lines[0];
        const companyName = lines[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
            }}
          >
            <span style={{ fontSize: "13px" }}>{fullName || "-"}</span>
            {companyName && (
              <span
                style={{
                  fontSize: "14px",
                  color: "#000",
                  fontStyle: "italic",
                  fontWeight: "bold",
                }}
              >
                {companyName}
              </span>
            )}
          </div>
        );
      },
    },

    {
      field: "phoneNumber",
      headerName: "موبایل",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 170,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "tagRef",
      headerName: "تگ ها",
      editable: false,
      width: 80,
      sortable: true,
      align: "center",
      headerAlign: "center",
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) => {
        const TagsId: string[] = params?.value?.split(",") || [];

        const listOfSelectedTags =
          TagsId.length > 0
            ? getTags.filter((tag: any) =>
                TagsId.includes(tag.value?.toString())
              )
            : [];

        if (listOfSelectedTags.length === 0) {
          return <span style={{ color: "#999", fontSize: "12px" }}>-</span>;
        }

        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "2px",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {listOfSelectedTags.map((tag, index) => (
              <BootstrapTooltip title={tag.item.caption} key={index}>
                <Chip
                  size="small"
                  //label={tag.item.caption}
                  sx={{
                    fontSize: 10,
                    backgroundColor: tag.item?.other || "#1976d2",
                    color: "white",
                    height: 20,
                    maxWidth: 80,
                    "& .MuiChip-label": {
                      padding: "0 6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </BootstrapTooltip>
            ))}
          </div>
        );
      },
    },

    {
      field: "isSendBalanceSms",
      headerName: "ارسال پیامک",
      align: "center",
      headerAlign: "center",
      type: "boolean",
      minWidth: 150,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, boolean>) => {
        const value = params.value === true;
        return (
          <Chip
            label={value ? "فعال" : "غیرفعال"}
            color={value ? "success" : "default"}
            size="small"
            sx={{ fontSize: 12, borderRadius: 1 }}
          />
        );
      },
    },

    {
      field: "balance",
      headerName: "مانده(ریال)",
      align: "center",
      type: "number",
      headerAlign: "center",
      editable: false,
      minWidth: 170,
      sortable: true, // این فیلد محاسبه شده است و در backend قابل سورت نیست
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, number>) => {
        const balance = params.value || 0;
        const balanceColor =
          balance > 0 ? "#d32f2f" : balance < 0 ? "#2e7d32" : "#000000";
        return (
          <span style={{ color: balanceColor, fontWeight: "bold" }}>
            {balance.toLocaleString()}
          </span>
        );
      },
    },
    {
      field: "realBalance",
      headerName: "مانده واقعی",
      align: "center",
      type: "number",
      headerAlign: "center",
      editable: false,
      minWidth: 180,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, number>) => {
        const realBalance = params.value ?? 0;
        const balanceColor =
          realBalance > 0 ? "#d32f2f" : realBalance < 0 ? "#2e7d32" : "#000000";
        return (
          <span style={{ color: balanceColor, fontWeight: "bold" }}>
            {realBalance.toLocaleString()}
          </span>
        );
      },
    },
    {
      field: "nationalCode",
      headerName: "کد ملی",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 120,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "codePersonel",
      headerName: "شماره شناسنامه",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 120,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "email",
      headerName: "ایمیل",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 200,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "province",
      headerName: "استان",
      align: "center",
      minWidth: 180,
      headerAlign: "center",
      editable: false,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) =>
        params.row.province?.name,
    },
    {
      field: "city",
      headerName: "شهر",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) =>
        params.row.city?.name,
    },
    {
      field: "postalCode",
      headerName: "کد پستی",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "caseNumber",
      headerName: "شماره پرونده",
      align: "center",
      headerAlign: "center",
      type: "number",
      editable: false,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "address",
      headerName: "آدرس",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: renderCellExpand,
    },

    {
      field: "birthDate",
      headerName: "تاریخ تولد",
      sortable: true,
      headerAlign: "center",
      align: "center",
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value
            ? moment
                .from(params.value, "en", "YYYY-MM-DD")
                .format("jYYYY/jMM/jDD")
            : "-"
        }`,
    },
    {
      field: "createdOn",
      headerName: "تاریخ ثبت",
      sortable: true,
      headerAlign: "center",
      align: "center",
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value &&
          moment.from(params.value, "en", "YYYY-MM-DD").format("jYYYY/jMM/jDD")
        }`,
    },
    {
      field: "modifiedOn",
      headerName: "آخرین تغییرات",
      headerAlign: "center",
      align: "center",
      sortable: true,
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) =>
        `${
          params.value &&
          moment.from(params.value, "en", "YYYY-MM-DD").format("jYYYY/jMM/jDD")
        }`,
    },
    // فیلدهای جدید اضافه شده
    {
      field: "fatherName",
      headerName: "نام پدر",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 150,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "telNumber",
      headerName: "تلفن ثابت",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 120,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "nationalIDBack",
      headerName: "سریال پشت کارت ملی",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 150,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "coTelNumber",
      headerName: "تلفن ثابت تجاری",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 150,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "baseActivityRef",
      headerName: "زمینه فعالیت تجاری",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 200,
      sortable: false, // در backend پشتیبانی نمی‌شود
      valueOptions: getBaseActivities,
      type: "singleSelect",
      cellClassName: "center-vertical",
    },
    {
      field: "coTaxFileClass",
      headerName: "کلاسه پرونده مالیاتی",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 180,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "roleTitle",
      headerName: "سمت",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 120,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "roleName",
      headerName: "نوع کاربر",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 120,
      sortable: true,
      cellClassName: "center-vertical",
    },
    {
      field: "isActive",
      headerName: "وضعیت",
      align: "center",
      headerAlign: "center",
      editable: false,
      minWidth: 100,
      sortable: true,
      type: "boolean",
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, boolean>) => (
        <Chip
          label={params.value ? "فعال" : "غیرفعال"}
          color={params.value ? "success" : "error"}
          variant="filled"
        />
      ),
    },
    {
      field: "link",
      headerName: "لینک",
      align: "center",
      headerAlign: "center",
      editable: false,
      sortable: false, // این فیلد قابل سورت نیست
      minWidth: 300, // کاهش از 550 به 300
      maxWidth: 400, // محدود کردن حداکثر عرض
      flex: 0, // عدم استفاده از flex برای این ستون
      cellClassName: "center-vertical",
      renderCell: (params: GridRenderCellParams<any, string>) => {
        if (!params.value) {
          return <span style={{ color: "#999", fontSize: "12px" }}>-</span>;
        }

        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              textDecoration: "none",
              fontSize: "12px",
              wordBreak: "break-all",
              maxWidth: "100%",
              display: "block",
            }}
            title={params.value}
          >
            {params.value}
          </a>
        );
      },
    },
  ];
};
